'use client';

import { useMemo } from 'react';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { getPresaleEndMs } from '@/lib/utils/getPresaleEndMs';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';

interface I_ActionButtonsProps {
  variant: 'predictions' | 'pools';
  presaleEndDate?: string;
  poolSlug?: string;
}

export function ActionButtons({
  variant,
  presaleEndDate,
  poolSlug
}: I_ActionButtonsProps) {
  const t = useTranslations();
  const isPredictions = variant === 'predictions';
  const isPools = variant === 'pools';

  // Check if presale has ended
  const isPresaleEnded = useMemo(() => {
    if (!isPools || !presaleEndDate) return true;
    const presaleEndMs = getPresaleEndMs(presaleEndDate);
    if (presaleEndMs === null) return true;
    return new Date(presaleEndMs) < new Date();
  }, [isPools, presaleEndDate]);

  // Determine navigation URL for pools
  const poolsButtonHref = useMemo(() => {
    if (!isPools) return '#';
    if (!isPresaleEnded) {
      // Presale not ended - go to pools-presale page
      return C_Routes[E_Routes.POOLS_PRESALE].link;
    }
    // Presale ended - go to /pools/{slug}
    if (poolSlug) {
      return `/pools/${poolSlug}`;
    }
    return C_Routes[E_Routes.POOLS_PRESALE].link; // Fallback
  }, [isPools, isPresaleEnded, poolSlug]);

  if (isPredictions) return null;

  return (
    <div
      className={cn(
        'flex gap-4 fixed bottom-10 left-0 z-10 w-full px-4 md:static md:px-0'
      )}
    >
      <Button
        className="w-full h-10 shadow-2xl max-md:shadow-[0px_25px_50px_-12px_#000000]"
        asChild
      >
        <Link href={poolsButtonHref}>
          {t('transactions.filter.swapTokens')}
        </Link>
      </Button>
    </div>
  );
}
